/**
 * Renders a cutscene's `TtmCommand` stream. The stage keeps its own 320×200
 * scene canvas because TTM drawing is cumulative — sprites, rects and lines
 * stamp onto whatever is already there — and the visible frame is that scene
 * plus the current palette fade.
 *
 * Moved out of `shell/intro/intro-stage.ts`, which re-exports the pieces its
 * own tests reach for (`fadeOpacity`/`fadeFromCommand`) under their original
 * names. `TITLE.TTM` only ever needed `loadScreen` and `drawSprite`, so this
 * module's behaviour for those two is unchanged — the intro's pixel-exact
 * capture never exercises anything added below for the other five scripts.
 *
 * Fades are the one inferred quantity, carried over unchanged from the intro
 * (see that module's original header): `FADE IN/OUT`'s wire argument is a
 * *speed*, not a wall-clock duration, so the two durations here are chosen to
 * sit inside the delay that follows each fade.
 *
 * What the other five scripts need beyond the intro's two ops, and how each
 * is rendered — all "surfaced structurally, not given rendering semantics"
 * by the interpreter (docs/formats/ttm-animation.md's Buffer/palette
 * semantics section), so this is where that semantics gets decided:
 *
 * - `drawFilledRect`/`drawEmptyRect`/`drawLine` need an actual palette to
 *   turn a wire colour index into RGB — the intro never draws by index
 *   because every pixel it shows comes pre-baked with its palette already
 *   applied. The five cutscenes' own draws are axis-aligned bars and 1px
 *   rules (the citation strip background and its bracket rules), so lines
 *   are filled as 1px-thick rects rather than stroked paths — exact on the
 *   pixel grid, no antialiasing to fight.
 * - `saveGetPutRegion`/`drawGetPut` are dgds's "erase the last animation
 *   frame" idiom: capture a rect of the scene once, then blit it back before
 *   the next brush frame draws over the same area. Implemented literally
 *   (capture to an offscreen canvas, redraw on demand) rather than left inert
 *   like the intro's `storeArea`/`saveBackground`, because the medal and
 *   salute scenes actually rely on it to avoid a smear of stacked poses.
 * - `storeArea`, `saveBackground`, `wipe`, `setPalette` and `unknown` stay
 *   no-ops, exactly as the intro already treats the two of these it happens
 *   to instruction — dgds gives none of them independent rendering semantics
 *   either (see the doc section above), and every wipe in the real corpus is
 *   immediately preceded by the full-screen clear it would otherwise animate.
 */

import type { TtmCommand } from "@red-opus/formats";
import { clearLogical, type LogicalSurface } from "../render/canvas-painter.ts";
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from "../render/draw-ops.ts";
import { type IndexedPalette, paletteCss } from "../render/ps-palette.ts";
import type { ShellAssets } from "../render/shell-assets.ts";

const FADE_MS: Readonly<Record<number, number>> = { 2: 600, 3: 400 };
const DEFAULT_FADE_MS = 500;

export interface FadeState {
  readonly direction: "in" | "out";
  readonly startedAtMs: number;
  readonly durationMs: number;
}

/** 0 = scene fully visible, 1 = fully black. */
export function fadeOpacity(fade: FadeState | undefined, nowMs: number): number {
  if (!fade) return 0;
  const progress = fade.durationMs <= 0 ? 1 : Math.min(1, Math.max(0, (nowMs - fade.startedAtMs) / fade.durationMs));
  return fade.direction === "in" ? 1 - progress : progress;
}

export function fadeFromCommand(command: TtmCommand, nowMs: number): FadeState | undefined {
  if (command.kind !== "fadeIn" && command.kind !== "fadeOut") return undefined;
  return {
    direction: command.kind === "fadeIn" ? "in" : "out",
    startedAtMs: nowMs,
    durationMs: FADE_MS[command.speed] ?? DEFAULT_FADE_MS,
  };
}

/** One captured rect, keyed by `SET GETPUT NUM`'s slot — dgds's "restore the clean background here" buffer. */
export interface GetPutRegion {
  readonly x: number;
  readonly y: number;
  readonly canvas: HTMLCanvasElement;
}

/** Everything one cutscene run threads through every command it applies. */
export interface CutsceneStageCtx {
  readonly scene: LogicalSurface;
  readonly assets: ShellAssets;
  /** The palette the last `LOAD PAL`/`SELECT PAL` resolved to, for the index-colour draw ops. `undefined` until one loads. */
  palette: IndexedPalette | undefined;
  readonly getput: Map<number, GetPutRegion>;
}

export function createCutsceneStageCtx(scene: LogicalSurface, assets: ShellAssets): CutsceneStageCtx {
  return { scene, assets, palette: undefined, getput: new Map() };
}

function drawScreen(scene: LogicalSurface, assets: ShellAssets, id: string): void {
  const image = assets.images.get(`screens/${id}`);
  if (!image) return;
  scene.context.drawImage(image, 0, 0);
}

function drawSprite(scene: LogicalSurface, assets: ShellAssets, command: Extract<TtmCommand, { kind: "drawSprite" }>): void {
  if (command.id === undefined) return;
  const sheetId = `sprites/${command.id}`;
  const sheet = assets.images.get(sheetId);
  const frame = assets.sprites.get(sheetId)?.[command.frame];
  if (!sheet || !frame) return;
  const flipH = command.flip === "h" || command.flip === "hv";
  const flipV = command.flip === "v" || command.flip === "hv";
  const { context } = scene;
  context.save();
  context.translate(command.x + (flipH ? frame.width : 0), command.y + (flipV ? frame.height : 0));
  context.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  context.drawImage(sheet, frame.x, frame.y, frame.width, frame.height, 0, 0, frame.width, frame.height);
  context.restore();
}

/** Axis-aligned only — every `DRAW LINE` the real corpus's five non-intro cutscenes emit is horizontal or vertical (see module header). */
function drawLine(scene: LogicalSurface, palette: IndexedPalette | undefined, command: Extract<TtmCommand, { kind: "drawLine" }>): void {
  if (!palette) return;
  const { context } = scene;
  context.fillStyle = paletteCss(palette, command.color);
  const { x1, y1, x2, y2 } = command;
  if (y1 === y2) {
    context.fillRect(Math.min(x1, x2), y1, Math.abs(x2 - x1) + 1, 1);
  } else if (x1 === x2) {
    context.fillRect(x1, Math.min(y1, y2), 1, Math.abs(y2 - y1) + 1);
  } else {
    context.beginPath();
    context.moveTo(x1 + 0.5, y1 + 0.5);
    context.lineTo(x2 + 0.5, y2 + 0.5);
    context.lineWidth = 1;
    context.strokeStyle = context.fillStyle;
    context.stroke();
  }
}

/** `fg` at the rect's own bounds, `bg` inset 1px — dgds's fallthrough from `DRAW FILLED RECT` into the empty-rect border draw (see module header). */
function drawFilledRect(
  scene: LogicalSurface,
  palette: IndexedPalette | undefined,
  command: Extract<TtmCommand, { kind: "drawFilledRect" }>,
): void {
  if (!palette) return;
  const { context } = scene;
  const { x, y, w, h } = command;
  context.fillStyle = paletteCss(palette, command.fg);
  context.fillRect(x, y, w, h);
  if (w > 2 && h > 2) {
    context.fillStyle = paletteCss(palette, command.bg);
    context.fillRect(x + 1, y + 1, w - 2, h - 2);
  }
}

function drawEmptyRect(
  scene: LogicalSurface,
  palette: IndexedPalette | undefined,
  command: Extract<TtmCommand, { kind: "drawEmptyRect" }>,
): void {
  if (!palette) return;
  const { context } = scene;
  const { x, y, w, h } = command;
  context.fillStyle = paletteCss(palette, command.color);
  context.fillRect(x, y, w, 1);
  context.fillRect(x, y + h - 1, w, 1);
  context.fillRect(x, y, 1, h);
  context.fillRect(x + w - 1, y, 1, h);
}

function saveGetPutRegion(ctx: CutsceneStageCtx, command: Extract<TtmCommand, { kind: "saveGetPutRegion" }>): void {
  const { x, y, w, h } = command;
  if (w <= 0 || h <= 0) return;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const context = canvas.getContext("2d");
  if (!context) return;
  context.drawImage(ctx.scene.canvas, x, y, w, h, 0, 0, w, h);
  ctx.getput.set(command.slot, { x, y, canvas });
}

function drawGetPut(ctx: CutsceneStageCtx, command: Extract<TtmCommand, { kind: "drawGetPut" }>): void {
  const region = ctx.getput.get(command.slot);
  if (!region) return;
  ctx.scene.context.drawImage(region.canvas, region.x, region.y);
}

/**
 * Applies one command's *visual* effect. Asset loads and palette selects
 * (other than resolving `ctx.palette` for the index-colour ops below) are
 * no-ops: the bake pipeline has already resolved every `.SCR`/`.BMP` to an
 * RGBA image with its palette applied, so there is no runtime palette to
 * switch for those. Sound commands are handled by the audio side; `unknown`
 * is the documented safe no-op.
 */
export function applyCutsceneCommand(ctx: CutsceneStageCtx, command: TtmCommand): void {
  if (command.kind === "loadScreen") drawScreen(ctx.scene, ctx.assets, command.id);
  else if (command.kind === "drawSprite") drawSprite(ctx.scene, ctx.assets, command);
  else if (command.kind === "loadPalette") ctx.palette = ctx.assets.palettes.get(command.id) ?? ctx.palette;
  else if (command.kind === "drawLine") drawLine(ctx.scene, ctx.palette, command);
  else if (command.kind === "drawFilledRect") drawFilledRect(ctx.scene, ctx.palette, command);
  else if (command.kind === "drawEmptyRect") drawEmptyRect(ctx.scene, ctx.palette, command);
  else if (command.kind === "saveGetPutRegion") saveGetPutRegion(ctx, command);
  else if (command.kind === "drawGetPut") drawGetPut(ctx, command);
}

export function resetCutsceneScene(scene: LogicalSurface): void {
  clearLogical(scene);
}

export function composeCutsceneFrame(target: LogicalSurface, scene: LogicalSurface, opacity: number): void {
  const { context } = target;
  context.drawImage(scene.canvas, 0, 0);
  if (opacity <= 0) return;
  context.save();
  context.globalAlpha = Math.min(1, opacity);
  context.fillStyle = "#000000";
  context.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
  context.restore();
}
